import { useEffect, useState } from "react";

export const PERMISSION_KEYS = [
  "super",
  "submissions:view",
  "submissions:edit",
  "submissions:delete",
  "notifications:edit",
  "progressStages:edit",
  "formEdit:view",
  "formEdit:edit",
  "formEdit:deleteSections",
  "formEdit:deleteQuestions"
] as const;

export type Permission = (typeof PERMISSION_KEYS)[number];

export const PERMISSION_LABELS: Record<Permission, string> = {
  super: "Super admin — change and edit everything",
  "submissions:view": "Submissions — viewer only",
  "submissions:edit": "Submissions — edit",
  "submissions:delete": "Submissions — delete",
  "notifications:edit": "Notifications — edit/delete",
  "progressStages:edit": "Progress stages — edit/delete",
  "formEdit:view": "Edit form — view only",
  "formEdit:edit": "Edit form — edit",
  "formEdit:deleteSections": "Edit form — delete sections",
  "formEdit:deleteQuestions": "Edit form — delete questions"
};

let cache: string[] | null = null;
let inflight: Promise<string[]> | null = null;

export async function fetchPermissions(force = false): Promise<string[]> {
  if (cache && !force) return cache;
  if (!inflight || force) {
    inflight = fetch("/api/me")
      .then((r) => (r.ok ? r.json() : {}))
      .then((j: { permissions?: string[] }) => {
        cache = Array.isArray(j.permissions) ? j.permissions : [];
        return cache;
      })
      .catch(() => {
        cache = [];
        return cache;
      });
  }
  return inflight;
}

export function permsHave(perms: Set<string> | string[], p: Permission): boolean {
  const set = perms instanceof Set ? perms : new Set(perms);
  return set.has("super") || set.has(p);
}

export interface PermissionsState {
  permissions: Set<string>;
  loading: boolean;
  has: (p: Permission) => boolean;
  isSuper: boolean;
}

export function usePermissions(): PermissionsState {
  const [perms, setPerms] = useState<string[] | null>(cache);
  useEffect(() => {
    let mounted = true;
    fetchPermissions().then((p) => {
      if (mounted) setPerms(p);
    });
    return () => {
      mounted = false;
    };
  }, []);
  const set = new Set(perms ?? []);
  return {
    permissions: set,
    loading: perms === null,
    has: (p) => set.has("super") || set.has(p),
    isSuper: set.has("super")
  };
}
